import { Package, Settings } from "lucide-react";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { TabSkeleton, TabError } from "../TabStatus";
import { formatDateShort } from "../format";
import type { PurchaseRow } from "../types";
import type { ManageUrl } from "../Profile";

/**
 * Ported from ProfileView.tsx's `getBundleStatus` (:167-174), `BundleProgressRow`
 * (:178-237) and `BundlesTab` (:267-318). `getBundleStatus`/`BundleProgressRow`
 * are exported so OverviewTab's "Your Active Bundles" card (ProfileView:239-264)
 * can reuse the exact same status/row logic instead of duplicating it.
 */
export function getBundleStatus(bundle: PurchaseRow) {
  const now = new Date();
  const remaining = bundle.sessionsInfo?.remaining ?? 0;
  const isExpired = !!bundle.eventDate && new Date(bundle.eventDate) <= now;
  const isUsedUp = remaining <= 0;
  const isActive = !isExpired && !isUsedUp;
  return { remaining, isExpired, isUsedUp, isActive };
}

export function BundleProgressRow({
  bundle,
  manageUrl,
  showStatusBadge = false,
}: {
  bundle: PurchaseRow;
  manageUrl: ManageUrl;
  showStatusBadge?: boolean;
}) {
  const total = bundle.sessionsInfo?.total || 0;
  const used = bundle.sessionsInfo?.used || 0;
  const { remaining, isExpired, isActive } = getBundleStatus(bundle);

  const progress = total > 0 ? (used / total) * 100 : 0;
  let progressColor = "bg-green-500";
  if (!isActive) progressColor = "bg-muted-foreground/40";
  else if (remaining <= 1) progressColor = "bg-red-500";
  else if (remaining <= Math.ceil(total * 0.3)) progressColor = "bg-yellow-500";

  return (
    <div className="space-y-3 rounded-lg bg-muted/30 p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{bundle.title}</span>
          {showStatusBadge && !isActive && (
            <Badge variant="secondary" className="text-xs">
              {isExpired ? "Expired" : "Used up"}
            </Badge>
          )}
        </div>
        <span className="text-sm text-muted-foreground">
          {remaining} of {total} remaining
        </span>
      </div>
      <div className="relative h-2 overflow-hidden rounded-full bg-muted">
        <div className={`absolute left-0 top-0 h-full ${progressColor} transition-all`} style={{ width: `${100 - progress}%` }} />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {bundle.eventDate && <p className="text-xs text-muted-foreground">Expires: {formatDateShort(bundle.eventDate)}</p>}
        <Button asChild size="sm" className="w-full sm:ml-auto sm:w-auto">
          <a href={manageUrl("bundles", bundle._id)} aria-label={`Manage ${bundle.title}`}>
            <Settings className="h-4 w-4" />
            Manage bundle
          </a>
        </Button>
      </div>
    </div>
  );
}

export function BundlesTab({
  allOrders,
  isLoading,
  isError,
  onRetry,
  manageUrl,
}: {
  allOrders: PurchaseRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  manageUrl: ManageUrl;
}) {
  if (isLoading) return <TabSkeleton rows={3} />;
  if (isError) return <TabError onRetry={onRetry} />;

  const bundles = allOrders.filter((o) => o.type === "Bundle");
  const activeBundles: PurchaseRow[] = [];
  const inactiveBundles: PurchaseRow[] = [];
  for (const bundle of bundles) {
    (getBundleStatus(bundle).isActive ? activeBundles : inactiveBundles).push(bundle);
  }

  if (bundles.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">You haven&apos;t purchased any bundles yet.</div>;
  }

  return (
    <div className="space-y-8">
      {activeBundles.length > 0 && (
        <div>
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <Package className="h-5 w-5" />
            Active Bundles ({activeBundles.length})
          </h3>
          <div className="space-y-4">
            {activeBundles.map((bundle) => (
              <BundleProgressRow key={bundle._id} bundle={bundle} manageUrl={manageUrl} />
            ))}
          </div>
        </div>
      )}
      {inactiveBundles.length > 0 && (
        <div>
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-muted-foreground">
            Expired / Used Up ({inactiveBundles.length})
          </h3>
          <div className="space-y-4">
            {inactiveBundles.map((bundle) => (
              <BundleProgressRow key={bundle._id} bundle={bundle} manageUrl={manageUrl} showStatusBadge />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
